import { TRACK_TYPE } from './Enums'

/**
 * Checks if a trackTypeId is a Pool sub-track (PreDig, FrontEnd, GradingPlumbing, etc.)
 */
function isPoolSubTrack(trackTypeId) {
    return trackTypeId >= TRACK_TYPE.PREDIG.id && trackTypeId <= TRACK_TYPE.PREP_PLASTER.id;
}

/**
 * Filters pipeline items by status using the activeTracks from tblserviceprojecttrack.
 * 
 * For Pool sub-tracks (7-12): also matches against legacy Pool track type (1)
 * 
 * @param {Array} listPipeline - The list of pipeline items from the API
 * @param {Number} statusId - The status ID to filter by
 * @param {Number} trackTypeId - The track type ID (from TRACK_TYPE enum)
 * @returns {Array} Filtered pipeline items
 */
export function filterByTrackStatus(listPipeline, statusId, trackTypeId) {
    if (!listPipeline || !Array.isArray(listPipeline)) return [];

    return listPipeline.filter(pip => {
        if (!pip.activeTracks || pip.activeTracks.length === 0) return false;

        return pip.activeTracks.some(t => {
            // For pool sub-tracks (7-12), also match against legacy Pool track type (1)
            const typeMatch = t.trackType === trackTypeId ||
                (isPoolSubTrack(trackTypeId) && t.trackType === TRACK_TYPE.POOL.id);
            return typeMatch && (t.status === statusId || t.statusOriginal === statusId);
        });
    });
}

/**
 * Checks if a pipeline item has a specific track active.
 * 
 * @param {Object} pip - The pipeline item
 * @param {Number} trackTypeId - The track type ID
 * @returns {Boolean}
 */
export function hasActiveTrack(pip, trackTypeId) {
    if (!pip.activeTracks || pip.activeTracks.length === 0) return false;
    return pip.activeTracks.some(t => t.trackType === trackTypeId);
}

/**
 * Gets the current status of a specific track for a pipeline item.
 * 
 * @param {Object} pip - The pipeline item
 * @param {Number} trackTypeId - The track type ID
 * @returns {Object|null} { status, statusOriginal } or null if track not active
 */
export function getTrackStatus(pip, trackTypeId) {
    if (!pip.activeTracks || pip.activeTracks.length === 0) return null;
    // Try exact match first
    let track = pip.activeTracks.find(t => t.trackType === trackTypeId);
    // For pool sub-tracks (7-12), also try legacy Pool type (1)
    if (!track && isPoolSubTrack(trackTypeId)) {
        track = pip.activeTracks.find(t => t.trackType === TRACK_TYPE.POOL.id);
    }
    if (track) {
        return { status: track.status, statusOriginal: track.statusOriginal };
    }
    return null;
}

/**
 * Gets the count of active tracks for a pipeline item.
 * 
 * @param {Object} pip - The pipeline item
 * @returns {Number} Number of active tracks
 */
export function getActiveTrackCount(pip) {
    if (!pip.activeTracks) return 0;
    return pip.activeTracks.filter(t => t.trackType != 6 && t.status != 19).length;
}

/**
 * Determines the card CSS class based on track status (red/yellow card logic per track).
 * 
 * @param {Object} pip - The pipeline item
 * @param {Number} statusId - The status of the current column/card
 * @param {Number} trackTypeId - The track type being viewed
 * @returns {String} CSS class name: 'card-with-problem', 'card-waiting-solve-problem', or ''
 */
export function getTrackCardClass(pip, statusId, trackTypeId) {
    const trackStatus = getTrackStatus(pip, trackTypeId);
    if (!trackStatus || !trackStatus.statusOriginal) return '';
    
    if (trackStatus.status < trackStatus.statusOriginal) {
        // Project has been backed up in this track
        if (trackStatus.status === statusId) {
            return 'card-with-problem'; // RED - current position
        }
    }
    return '';
}
